import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import _ from 'lodash/fp';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { NavLink, useHistory } from 'react-router-dom';
import { SIGN_IN, SEND_OTP, VALIDATE_OTP } from '@api';
import { useMutation } from '@apollo/client';
import Loader from '../components/Spinner';
import NavBar from '../NewLanding/components/newNavbar';
import OtpInput from 'react-otp-input'

import {
  Container,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Input,
	Modal,
	ModalBody,
	ModalHeader,
	Spinner
} from 'reactstrap';
import DivisionTitle, { DivisionSubTitle } from '../NewLanding/components/divisionTitle';

toast.configure();

// Mirrors Mutations::Users::ValidateOtp::EXPIRED on the Rails side. If that
// copy ever changes, the modal simply stays open instead of returning to the
// password form — a soft failure, not a lockout.
const SIGN_IN_EXPIRED = 'Your sign-in expired. Please sign in again.';
// `signin` returns this literal as its authenticationToken. Real auth is the
// session cookie; App.js clears localStorage on any other value.
const LEGACY_AUTH_TOKEN = '12345';
const OTP_LENGTH = 6;

export default function Login(props) {
  const { register, handleSubmit, errors } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const [progress, setProgress] = useState(false);
  const history = useHistory();

  // The server enforces 2FA inside `signin` itself: a user with OTP on gets
  // back no token, no errors, and requireOtpConfirmation: true. Never assume
  // `errors` is an array here — reading `.map` off that null is exactly what
  // locked 2FA users out of this dashboard.
  const [signInUser, { data: signinData, loading, error }] = useMutation(
    SIGN_IN,
    {
      onCompleted: (signinData) => {
        const result = signinData?.signin;

        if (!result) {
          toast('Oops, something went wrong!', { type: 'error' });
          setProgress(false);
          return;
        }

        if (result.errors?.length > 0) {
          result.errors.forEach((err) => toast(err, { type: 'error' }));
          setProgress(false);
          return;
        }

        if (result.requireOtpConfirmation) {
          setOtp('');
          setShowOtpModal(true);
          setProgress(false);
          if (result.message) {
            toast(result.message, { type: 'success' });
          }
          return;
        }

        if (result.authenticationToken) {
          saveCredentials(result.user, result.authenticationToken);
          redirectToDashboard();
          return;
        }

        toast(result.message || 'Email or password is invalid', {
          type: 'error',
        });
        setProgress(false);
      },
      onError: (e) => {
        console.log(e);
        toast(e, { type: 'error' });
        setProgress(false);
      },
    }
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
	const [showOtpModal, setShowOtpModal] = useState(false);
	const [otp, setOtp] = useState('');

	// The pending sign-in is gone server-side — close the code step and hand the
	// user back to the password form, which is the only way to get a new code.
	function abandonOtp(message) {
		setShowOtpModal(false);
		setOtp('');
		setProgress(false);
		toast(message, { type: 'error' });
	}

	useEffect(() => {
		if (showOtpModal) {
			setTimeout(() => {
				document.getElementById("otpInput")?.focus()
			}, 1000)
		}
	}, [showOtpModal])

	// Resend only. `sendOtp` is no longer part of signing in — the password step
	// is what issues the first code.
	const [sendOtp] = useMutation(SEND_OTP, {
		onCompleted: (data) => {
			const result = data?.sendOtp;

			if (result?.errors?.length > 0) {
				toast(result.message || result.errors[0], { type: 'error' });
				return;
			}

			if (result?.requireOtpConfirmation) {
				toast(result.message, { type: 'success' });
				return;
			}

			// No pending sign-in left on the server, so there is nothing to resend.
			abandonOtp(SIGN_IN_EXPIRED);
		},
		onError: (e) => {
			console.log(e);
			toast('Could not resend the code. Please try again.', { type: 'error' });
		}
	});

	const [validateOtp] = useMutation(VALIDATE_OTP, {
		onCompleted: (data) => {
			const result = data?.validateOtp;

			if (result?.user) {
				setShowOtpModal(false);
				setOtp('');
				// Same sentinel `signin` returns — App.js force-logs-out on any other
				// value. The real credential is the session cookie, not this.
				saveCredentials(result.user, LEGACY_AUTH_TOKEN);
				redirectToDashboard();
				return;
			}

			// The server drops the pending marker once the code expires or the
			// attempt cap is hit; the only way forward is a fresh password step.
			if (result?.message === SIGN_IN_EXPIRED) {
				abandonOtp(SIGN_IN_EXPIRED);
				return;
			}

			toast(result?.message || 'Invalid code', { type: 'error' });
			setOtp('');
			setProgress(false);
		},
		onError: (e) => {
			console.log(e);
			// Never hand the Error object itself to toast — react-toastify renders
			// its argument as a React child, and an object there throws.
			toast(e.message || 'Could not verify the code. Please try again.', { type: 'error' });
			setOtp('');
			setProgress(false);
		}
	});

  const isLogin = () => {
    if (props.location.state) {
      toast(props.location.state.message, {
        type: props.location.state.type || 'error',
      });
    }

    if (localStorage.getItem('auth_token') !== null) {
      redirectToDashboard();
    }
  };

  useEffect(() => {
    isLogin();
  }, []);

  const redirectToDashboard = (role = localStorage.getItem('user_type')) => {
    role = role && localStorage.getItem('user_type');
    switch (role) {
      case 'admin': {
        history.push({
          pathname: '/admin-dashboard',
        });
        break;
      }
      case 'user': {
        history.push({
          pathname: '/properties',
        });
        break;
      }
      case 'va': {
        history.push({
          pathname: '/assistant-dashboard',
        });
        break;
      }
      default: {
        history.push({
          pathname: '/logout',
        });
        break;
      }
    }
  };

  // Called from BOTH login paths — `signin` (2FA off) and `validateOtp` (2FA
  // on) — so it takes the user object rather than a mutation payload.
  const saveCredentials = (user, token) => {
		localStorage.clear();
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_name', user.name);
    localStorage.setItem('user_email', user.email);
    localStorage.setItem('default_property_view', user.defaultPropertyView);
    if (user.defaultCityId) {
      localStorage.setItem('default_city_id', user.defaultCityId);
    }
    if (user.isAdmin === true) {
      //TODO: Remove the line below storing admin_token in local storage
      console.log('ADMIN LOGGED IN');
      localStorage.setItem('user_type', 'admin');
    } else {
			if (user.isBroker === true) {
				localStorage.setItem('is_broker', 'true');
			} else if (user.isAgent === true) {
				localStorage.setItem('is_agent', 'true');
        localStorage.setItem('can_send_invoices', user.canSendInvoices);
			}
      if (user.isVa === true) {
        //TODO: Remove the line below storing assistant_token in local storage
        console.log('VA LOGGED IN');
        localStorage.setItem('user_type', 'va');
      } else {
        //TODO: Remove the line below storing user_token in local storage
        localStorage.setItem('subscription_popup', true);

        if (user.subscriptions.length > 0) {
          let hasActiveSubscription = false;
          let hasTrialSubscription = false;
          user.subscriptions.map((subscription) => {
            if (
              subscription.isTrial === true &&
              subscription.active === true &&
              new Date(subscription.currentEndDatetime) > new Date()
            ) {
              hasTrialSubscription = true;
              localStorage.setItem(
                'trial_end',
                subscription.currentEndDatetime
              );
              localStorage.removeItem('subscription_popup');
            } else if (
              subscription.active === true &&
              new Date(subscription.currentEndDatetime) > new Date()
            ) {
              hasActiveSubscription = true;
              localStorage.removeItem('subscription_popup');
            }
          });
          if (hasActiveSubscription) {
            localStorage.removeItem('trial_end');
          }
        }
        localStorage.setItem('user_type', 'user');
        localStorage.setItem('is_profile_completed', user.isProfileCompleted);
      }
    }
  };

  const loginUser = () => {
    setProgress(true);
    signInUser({
      variables: {
        email: email,
        password: password,
      },
    });
  };

	function handleOtpSend(){
		sendOtp();
		document.getElementById("otpInput")?.focus()
	}

  const onSubmit = async (data, e) => {
    e.preventDefault();
		// The password step is what issues the code now — asking for an OTP before
		// the server knows who is signing in gets you nothing.
		loginUser();
  };

	// Anything the user pastes or an SMS autofills is normalised to at most six
	// digits, so `otp` can never overshoot the length that triggers submission.
	const handleOtpChange = (value) => {
		setOtp((value || '').replace(/\D/g, '').slice(0, OTP_LENGTH));
	};

	const handleOtpValidation = () => {
		if (otp?.length === OTP_LENGTH){
			validateOtp({
				variables: {
					otp: otp
				}
			});
			setProgress(true);
		} else {
			toast("Enter Valid OTP", { type: 'error' });
		}
	};

	useEffect(() => {
		if (otp?.length === OTP_LENGTH) {
			handleOtpValidation();
		}
	}, [otp])

  return (
    <>
      {progress ? <Loader /> : null}
      <NavBar />
      {/* <Container fluid className='py-5 text-center banner banner-small'>
        <Container className='my-5'>
          <h1>Login</h1>
        </Container>
      </Container> */}

			{ showOtpModal && 
				<Modal
					isOpen={true}
					// toggle={() => setShowOtpModal(!showOtpModal)}
					centered
					className='payment-method text-center'
				>
					<ModalHeader toggle={() => { setShowOtpModal(false); setOtp(''); setProgress(false); }}>Two-factor Authentication</ModalHeader>
					<ModalBody className='inputs-icons'>
						<p>We sent you a 6-digit OTP to verify your identity</p>
						{/* <OtpInput
							value={otp}
							onChange={setOtp}
							numInputs={6}
							shouldAutoFocus={true}
							renderSeparator={<span>&nbsp;</span>}
							renderInput={(props) => <input {...props} />}
							containerStyle='user-otp-input'
							inputStyle='otp-input'
						/> */}
						{/* maxLength, not maxlength — React silently drops the lowercase
						    form, which left this field unbounded. An overshoot past six
						    characters could never be submitted, because the only submit
						    path is the exact-length effect below. */}
						<input
							id="otpInput"
							className='otp-input-field'
							value={otp}
							maxLength={OTP_LENGTH}
							size="17"
							inputMode="numeric"
							onChange={e => handleOtpChange(e.target.value)}
							autoComplete="one-time-code"
						/>
						<div className='text-primary mt-3'> <a onClick={handleOtpSend}>Resend OTP</a> </div>
					</ModalBody>
					{ progress &&
						<center>
							<Spinner
								color="primary"
								type="grow"
							>
								Loading...
							</Spinner>
						</center>
					}
					{/* Insurance: the six-digit effect normally submits on its own, but
					    without a button any state it fails to reach is a dead end. Gate
					    it on `progress` ONLY — gating on length too would re-close that
					    escape hatch, since the effect fires at exactly six digits. */}
					<div>
						<Button color='green' className='button-dashboard mb-3 mt-2 w-25' disabled={progress} onClick={handleOtpValidation}>
							Verify
						</Button>
					</div>
				</Modal>
			}

      <Container fluid className='py-5em mt-5em text-center'>
        <Container>
          <Row className='px-lg-5'>
            <Col md='12'>
              <DivisionTitle
                className="mb-4"
                title={<>Let's <span className="highlighted-text">get started.</span></>}
              />
              <DivisionSubTitle
                className="mb-5"
                title="Getting leases is about to become much easier."
              />
              {/* SIGNUP_DISABLED_2026_05 — public signup closed
              <p>
                <small>
                  Don't have an account? <NavLink to='/signup'>Sign Up</NavLink>
                </small>
              </p>
              */}
            </Col>
						{ localStorage.getItem('session_expired') === "true" && 
							<Col md={{ size: 4, offset: 4 }}>
							<p className='error-msg'>Your session has expired. Please login again.</p>
						</Col>}
						{ localStorage.getItem('api_key_expired') === "true" && 
							<Col md={{ size: 4, offset: 4 }}>
							<p className='error-msg'>Your Spark API key has been revoked. Please contact to admin.</p>
						</Col>}
            <Col md={{ size: 4, offset: 4 }} className='inputFields'>
              <Form className='form' onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <Input
                    type='text'
                    placeholder='Email'
                    name='email'
                    autoComplete='off'
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    innerRef={register({
                      required: true,
                      pattern: /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/,
                    })}
                  />
                  {_.get('email.type', errors) === 'required' && (
                    <p className='input-error'>* Email required.</p>
                  )}
                  {_.get('email.type', errors) === 'pattern' && (
                    <p className='input-error'>* Invalid Email.</p>
                  )}
                </FormGroup>

                <FormGroup>
                  <Input
                    type='password'
                    placeholder='Password'
                    name='password'
                    autoComplete='off'
                    innerRef={register({
                      required: true,
                    })}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                  />
                  {_.get('password.type', errors) === 'required' && (
                    <p className='input-error'>* Password required</p>
                  )}
                </FormGroup>

                <Button color='green' size='lg' className="btn-rounded" disabled={progress}>
                  {progress ? 'Signing in...' : 'Login'}
                </Button>
                <p className='mt-3'>
                  <small>
                    <NavLink to='/forgot-password'>Trouble logging in?</NavLink>
                  </small>
                </p>
              </Form>
            </Col>
          </Row>

          <Row className='pt-5 privacy-policy'>
            <Col md={{ size: 4, offset: 4 }}>
              <p className='mb-0'>
                <small>By Continuing you agree to accept our</small>
              </p>
              <p className='mb-5'>
                <small>
                  <NavLink to='/privacy-policy'>Privacy Policy</NavLink> &{' '}
                  <NavLink to='/terms-of-service'>Terms of Service.</NavLink>
                </small>
              </p>
              <hr className='border-light' />
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
}
